import { useCallback, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { SessionUser } from 'src/shared/session';

import { useCurrentAuthenticatedUser } from 'src/app/shared/hooks/session';
import { isError, isLoading } from 'src/app/shared/utils/async';

import { VANITY_ERROR_MAP } from '../../../../../constants';
import { parseError } from '../../../../../utils';
import { useVanityModalState } from '../../../../modalContext/context';
import { getVanityUrl } from '../useCreateVanityUrl/api';

type MutationArgs = { customExpression: string; user: SessionUser | undefined; utilityContext: UtilityContext };

type UseCreateVanityLinkReturn = ModelHookResult<
	{ loading: boolean; urlError: string[] | null; wordCount: number; vanityUrl: string },
	{
		handleChange: (event: React.ChangeEvent<HTMLInputElement> | string) => void;
		handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
	}
>;

type Props = { petitionId: string; displayedSignatures: number };
// eslint-disable-next-line max-lines-per-function
export function useCreateVanityUrlForm({ petitionId, displayedSignatures }: Props): UseCreateVanityLinkReturn {
	const { translate } = useI18n();
	const track = useTracking();
	const trackingData = { petition_id: `petition_${petitionId}`, num_signatures: displayedSignatures };

	const [{ vanityUrl }, { setViewState, setVanityUrl }] = useVanityModalState();

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const {
		mutate: createUrl,
		reset,
		...createUrlState
	} = useMutation({
		mutationFn: async ({ utilityContext, ...args }: MutationArgs) => getVanityUrl(args, utilityContext),
		onSuccess: () => {
			void track('custom_url_create_submit_click', trackingData);
			setViewState('CONFIRM');
		},
		onError: (error) => {
			void track('custom_url_create_error', trackingData);
			if (parseError(error).includes(VANITY_ERROR_MAP.UNAUTHENTICATED.code)) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join/redirected?redirectTo=${params}`, { forceMode: 'external' });
			}
		},
	});

	const urlError = useMemo(() => {
		if (isError(createUrlState))
			return parseError(createUrlState.error).map((code) => translate(VANITY_ERROR_MAP[code].message));
		return null;
	}, [createUrlState, translate]);

	const loading = isLoading(createUrlState);

	const utilityContext = useUtilityContext();
	const user = useCurrentAuthenticatedUser();
	const handleSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const formData = new FormData(event.currentTarget);
			const url = formData.get('vanity-url-input') as string;

			createUrl({ customExpression: url, user, utilityContext });
			setVanityUrl(url);
		},
		[createUrl, user, utilityContext, setVanityUrl],
	);

	const [wordCount, setWordCount] = useState(100);
	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement> | string) => {
			if (urlError && urlError.length > 0) {
				reset();
			}
			const value = typeof e === 'string' ? e : e.currentTarget.value;
			setVanityUrl(value);

			setWordCount(100 - value.length);
		},
		[reset, setVanityUrl, urlError],
	);

	return {
		data: { loading, wordCount, urlError, vanityUrl },
		actions: { handleChange, handleSubmit },
	};
}
